import { createContext, useContext, type ReactNode, type RefObject } from 'react'
import type { AgentV2ArtifactEditorRef } from '@/agent-v2/artifact-editor/components/AgentV2ArtifactEditor'

/**
 * The open draft's DOCX export surface, shared by the editor toolbar (Add Template) and the draft
 * header (download / save), which render in separate trees.
 */
export type DraftExportScope = {
  /* The Add Template control for the editor toolbar. Null when the user cannot change the template. */
  templateButton: ReactNode
  /* The draft editor registers here so an export can flush unsaved edits before reading the head. */
  editorRef: RefObject<AgentV2ArtifactEditorRef>
  /* Downloads through the template, applying it first when needed. Absent when no template is selected and none has been applied. */
  onDownloadWithTemplate?: () => void
  /* Saves a copy of the draft to matter files. Absent when saving is unavailable to this user. */
  onSave?: () => void
  /* Opens the matter's folder of exported .docx drafts; resolves once it has navigated or shown a toast. Absent when Export as .docx is off. */
  onViewExports?: () => Promise<void>
  /* True while an export is applying a template, saving to matter files, or persisting a template selection. */
  isExporting: boolean
  /* True while the Export as .docx flag is still loading for the draft's creator. Which download and save apply depends on it, so neither can start yet. */
  isLoadingExportActions?: boolean
}

export const DraftExportContext = createContext<DraftExportScope | null>(null)

/** The draft's export surface. Throws outside a provider (a setup bug, not a runtime state). */
export function useDraftExport(): DraftExportScope {
  const scope = useContext(DraftExportContext)
  if (!scope) {
    throw new Error('useDraftExport must be used within a DraftExportProvider')
  }
  return scope
}
